'use client'

import { useEffect, useRef, useState } from 'react'

import { instagramEmbedSrc, type InstagramEmbedRef } from './instagram'

/**
 * Iframe oficial do Instagram com ALTURA EXATA — sem o `embed.js` deles.
 *
 * O iframe `instagram.com/<p|reel|tv>/<código>/embed/` avisa o pai da própria altura por
 * `postMessage` (`{"type":"MEASURE","details":{"height":N}}`) — é exatamente como o `embed.js` do
 * Instagram redimensiona. Aqui ouvimos essa mensagem com 20 linhas em vez de carregar o script
 * de terceiros (~100 KB) em toda página com post.
 *
 * SSR primeiro: o iframe já vai no HTML com uma altura inicial generosa (post de imagem quadrada
 * ≈ 54 de cabeçalho + 540 de imagem + ações + legenda). Com 720 fixos, a legenda e o «ver no
 * Instagram» ficavam CORTADOS (medido em 19/08/2026 no comunicado da Bitunix); com a medida real,
 * nem corta nem sobra branco.
 *
 * Segurança: só aceita mensagens cuja `origin` seja o Instagram E cuja `source` seja ESTE iframe —
 * outra aba/iframe não consegue redimensionar este.
 */
const ALTURA_INICIAL: Record<InstagramEmbedRef['kind'], number> = { p: 760, reel: 960, tv: 960 }
const ALTURA_MIN = 200
const ALTURA_MAX = 2400

export function InstagramFrame({
  item,
  className,
}: {
  item: InstagramEmbedRef
  className?: string
}) {
  const ref = useRef<HTMLIFrameElement>(null)
  const [altura, setAltura] = useState<number>(ALTURA_INICIAL[item.kind])

  useEffect(() => {
    const onMessage = (ev: MessageEvent) => {
      if (ev.origin !== 'https://www.instagram.com') return
      if (!ref.current || ev.source !== ref.current.contentWindow) return
      try {
        const data = typeof ev.data === 'string' ? JSON.parse(ev.data) : ev.data
        const h = Number(data?.details?.height)
        if (data?.type === 'MEASURE' && Number.isFinite(h) && h >= ALTURA_MIN && h <= ALTURA_MAX) {
          setAltura(Math.ceil(h))
        }
      } catch {
        /* mensagem de outro formato: ignora */
      }
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [])

  return (
    <div
      className={['embed-block embed-block--instagram', className].filter(Boolean).join(' ')}
      // Centrado no post por padrão (pedido do dono, 19/08/2026), como o embed do X.
      style={{ maxWidth: 540, width: '100%', marginLeft: 'auto', marginRight: 'auto' }}
    >
      {/*
       * Moldura (pedido do dono, 19/08/2026): o iframe do Instagram vem sem borda e «flutuava»
       * no texto; o tweet do react-tweet já tem borda e cantos arredondados — aqui é a mesma
       * linguagem, com os tokens do tema (`--border`, `--radius`) para seguir claro/escuro.
       * `overflow: hidden` arredonda o conteúdo do iframe junto com a moldura.
       */}
      <div
        style={{
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius, 0.625rem)',
          overflow: 'hidden',
          background: '#fff',
        }}
      >
        <iframe
          ref={ref}
          src={instagramEmbedSrc(item)}
          title="Publicação do Instagram"
          loading="lazy"
          scrolling="no"
          frameBorder={0}
          allowFullScreen
          allow="encrypted-media; clipboard-write"
          style={{ display: 'block', width: '100%', height: altura, border: 0, background: '#fff' }}
        />
      </div>
    </div>
  )
}
